import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import { getCampaigns, saveCampaign, deleteCampaign } from "../../actions/ads";
import { Toggle } from "../settings/AlertCard";
import { primaryButton, secondaryButton, smallButton } from "../template/templateLabels";
import { formatCount } from "../dashboard/incomeLabels";
import CampaignForm, { bodyOf } from "./CampaignForm";
import Creatives from "./Creatives";
import {
	AdTabs,
	DELIVERY,
	card,
	campaignStatus,
	deliveryOf,
	durationText,
	periodText,
	scheduleText,
	targetsOf,
} from "./adLabels";

// The ads on the kiosk screens: each campaign with its videos and images, the days
// it runs and the kiosks it plays on (the backend's routes/api/ads.js). On top, each
// kiosk and whether it has taken its newest list, which it does within one heartbeat
// of a change. Kept on the page, not in the store.

const dangerButton =
	"inline-flex items-center px-3 py-1 text-xs font-medium leading-5 text-red-600 transition-colors duration-150 bg-white border border-red-200 rounded-lg dark:text-red-400 dark:bg-gray-700 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-gray-600 focus:outline-none";

// null: the form is closed; NEW: adding a campaign; otherwise the one being edited
const NEW = "new";

const KioskStrip = ({ printers }) => (
	<section className={"p-4 mb-6 " + card}>
		<div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
			<h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Киоскууд</h2>
			<p className="text-xs text-gray-500 dark:text-gray-400">
				{Object.entries(DELIVERY).map(([key, item]) => (
					<span key={key} className="inline-flex items-center ml-3" title={item.hint}>
						<span className={"w-2 h-2 mr-1 rounded-full " + item.dot} />
						{item.label}
					</span>
				))}
			</p>
		</div>
		<div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{printers.map((printer) => {
				const delivery = deliveryOf(printer);
				// A kiosk that runs but showed no ad in its last hour: its screen may
				// have hidden the page
				const silent = printer.online && printer.lastHour && printer.lastHour.shownSec === 0;
				return (
					<div
						key={printer._id}
						className="flex items-start gap-2 px-3 py-2 border border-gray-100 rounded-lg dark:border-gray-700"
						title={delivery.hint}
					>
						<span className={"flex-shrink-0 w-2 h-2 mt-1.5 rounded-full " + delivery.dot} />
						<div className="min-w-0">
							<p className="text-sm font-medium text-gray-700 truncate dark:text-gray-200">
								{printer.name}
								{printer.online === false && (
									<span className="ml-1.5 text-xs font-normal text-red-500">офлайн</span>
								)}
							</p>
							<p className="text-xs text-gray-500 truncate dark:text-gray-400">
								{delivery.label}
								{printer.delivery !== "unsupported" && ` · ${printer.items} бүтээл`}
							</p>
							{printer.lastHour && (
								<p className={"text-xs " + (silent ? "text-red-600 dark:text-red-400" : "text-gray-400")}>
									Сүүлийн цагт {durationText(printer.lastHour.shownSec)}
									{printer.lastHour.largeSec > 0 && ` (бүтэн дэлгэцээр ${durationText(printer.lastHour.largeSec)})`}
								</p>
							)}
						</div>
					</div>
				);
			})}
		</div>
	</section>
);

const CampaignCard = ({ campaign, printers, onChange, onEdit, onRemoved, saveCampaign, deleteCampaign }) => {
	const [busy, setBusy] = useState(false);
	const status = campaignStatus(campaign);
	const targets = targetsOf(campaign, printers);
	const ready = targets.filter((printer) => printer.delivery === "ready").length;
	// The kiosks are sent it, in its hours or not
	const sent = ["live", "scheduled", "offHours"].includes(status.key);

	const setEnabled = async (enabled) => {
		setBusy(true);
		const saved = await saveCampaign(
			campaign._id,
			{ ...bodyOf(campaign), enabled },
			enabled ? "Киоскуудад илгээгдэнэ" : "Унтраалаа",
		);
		setBusy(false);
		if (saved) onChange(saved);
	};

	const remove = async () => {
		if (!window.confirm(`"${campaign.name}" кампанит ажлыг бүтээлүүдийн хамт устгах уу?`)) return;
		setBusy(true);
		const removed = await deleteCampaign(campaign._id);
		setBusy(false);
		if (removed) onRemoved(campaign._id);
	};

	return (
		<article className={"mb-4 " + card}>
			<div className="flex flex-wrap items-start gap-4 p-5 border-b border-gray-100 dark:border-gray-700">
				{/* Full width on a phone, so the buttons go below it */}
				<div className="w-full min-w-0 sm:w-auto sm:flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">{campaign.name}</h3>
						<span className={"px-2 py-0.5 text-xs rounded-full " + status.badge}>{status.label}</span>
					</div>
					<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
						{campaign.advertiser || "Өөрсдийн зар"}
						{" · "}
						{periodText(campaign)}
						{scheduleText(campaign.schedule) && ` · ${scheduleText(campaign.schedule)}`}
						{" · "}
						{campaign.printers.length === 0 ? "Бүх киоск" : `${campaign.printers.length} киоск`}
						{campaign.weight > 1 && ` · ${campaign.weight} дахин`}
						{campaign.weights.length > 0 && ` · ${campaign.weights.length} киоскт өөр давтамжтай`}
					</p>
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						{campaign.plays > 0
							? `${formatCount(campaign.plays)} тоглолт · ${durationText(campaign.shownSec)} харагдсан`
							: "Тоглолт бүртгэгдээгүй"}
						{sent && targets.length > 0 && (
							<span className={ready === targets.length ? "text-green-600 dark:text-green-400" : ""}>
								{" · "}
								{ready}/{targets.length} киоскт бэлэн
							</span>
						)}
					</p>
				</div>
				<div className="flex items-center gap-2">
					<Toggle
						checked={campaign.enabled}
						onChange={(enabled) => !busy && setEnabled(enabled)}
						label={campaign.enabled ? "Унтраах" : "Асаах"}
					/>
					<button
						className={dangerButton + (busy || campaign.plays > 0 ? " opacity-40 cursor-not-allowed" : "")}
						disabled={busy || campaign.plays > 0}
						onClick={remove}
						title={campaign.plays > 0 ? "Тоглолт бүртгэгдсэн тул тайланд үлдэнэ. Унтрааж болно." : undefined}
					>
						<i className="mr-1.5 fas fa-trash-alt" />
						Устгах
					</button>
					<button
						className={smallButton + " inline-flex items-center"}
						disabled={busy}
						onClick={() => onEdit(campaign)}
					>
						<i className="mr-1.5 fas fa-pen" />
						Засах
					</button>
				</div>
			</div>
			<div className="p-5">
				{status.key === "empty" && (
					<p className="mb-3 text-xs text-yellow-700 dark:text-yellow-300">
						<i className="mr-1.5 fas fa-exclamation-triangle" />
						Асаалттай ч тоглох бүтээл алга. Бүтээл нэмэх эсвэл асаана уу.
					</p>
				)}
				<Creatives campaign={campaign} onChange={onChange} />
			</div>
		</article>
	);
};

const Campaigns = ({ getCampaigns, saveCampaign, deleteCampaign }) => {
	const [page, setPage] = useState(null);
	const [loading, setLoading] = useState(true);
	const [editing, setEditing] = useState(null);

	const load = useCallback(async () => {
		setLoading(true);
		const loaded = await getCampaigns();
		setLoading(false);
		if (loaded) setPage(loaded);
	}, [getCampaigns]);

	useEffect(() => {
		load();
	}, [load]);

	// What an edit returns takes the campaign's place at once. The kiosks' state is
	// loaded again: a changed list is new to every kiosk it goes to.
	const replace = (saved) => {
		setPage((current) => {
			const exists = current.campaigns.some((item) => item._id === saved._id);
			return {
				...current,
				campaigns: exists
					? current.campaigns.map((item) => (item._id === saved._id ? saved : item))
					: [saved, ...current.campaigns],
			};
		});
		getCampaigns().then((loaded) => loaded && setPage(loaded));
	};
	const removed = (id) =>
		setPage((current) => ({ ...current, campaigns: current.campaigns.filter((item) => item._id !== id) }));

	return (
		<Admin>
			{/* min-w-0: the layout's grid would otherwise widen the page on a phone */}
			<div className="min-w-0 pb-10">
				<div className="flex flex-wrap items-end justify-between gap-4 mt-6 mb-4">
					<div>
						<h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-200">Реклам</h2>
						<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
							Киоскийн дэлгэцийн баруун дээд буланд, хэрэглэгчгүй үед бүтэн дэлгэцээр гарах зар
						</p>
					</div>
					<div className="flex gap-2">
						<button className={secondaryButton} onClick={load} disabled={loading} title="Киоскуудын төлөвийг шинэчлэх">
							<i className={"fas fa-sync-alt" + (loading ? " fa-spin" : "")} />
						</button>
						<button
							className={primaryButton + " inline-flex items-center whitespace-nowrap"}
							onClick={() => setEditing(NEW)}
							disabled={!page}
						>
							<i className="mr-2 fas fa-plus" />
							Шинэ кампанит ажил
						</button>
					</div>
				</div>
				<AdTabs active="/ads" />

				{!page ? (
					loading ? (
						<img src={spinner} className="my-6" style={{ width: "50px", margin: "auto", display: "block" }} alt="Loading..." />
					) : (
						<div className={"flex flex-wrap items-center justify-between gap-4 p-5 " + card}>
							<p className="text-sm text-gray-600 dark:text-gray-300">
								<i className="mr-2 text-red-500 fas fa-exclamation-circle" />
								Зарын мэдээллийг ачаалж чадсангүй
							</p>
							<button type="button" className={secondaryButton} onClick={load}>
								Дахин оролдох
							</button>
						</div>
					)
				) : (
					<>
						{page.printers.length > 0 && <KioskStrip printers={page.printers} />}
						{page.campaigns.length === 0 ? (
							<div className={"p-10 text-center " + card}>
								<p className="text-sm text-gray-600 dark:text-gray-300">Кампанит ажил алга.</p>
								<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
									Зар байхгүй үед киоскууд өөрсдийн зараа (reclam.mp4) тоглуулна.
								</p>
							</div>
						) : (
							page.campaigns.map((campaign) => (
								<CampaignCard
									key={campaign._id}
									campaign={campaign}
									printers={page.printers}
									onChange={replace}
									onEdit={setEditing}
									onRemoved={removed}
									saveCampaign={saveCampaign}
									deleteCampaign={deleteCampaign}
								/>
							))
						)}
					</>
				)}

				{editing && page && (
					<CampaignForm
						campaign={editing === NEW ? null : editing}
						printers={page.printers}
						onSaved={replace}
						onClose={() => setEditing(null)}
					/>
				)}
			</div>
		</Admin>
	);
};

KioskStrip.propTypes = {
	printers: PropTypes.array.isRequired,
};

CampaignCard.propTypes = {
	campaign: PropTypes.object.isRequired,
	printers: PropTypes.array.isRequired,
	onChange: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired,
	onRemoved: PropTypes.func.isRequired,
	saveCampaign: PropTypes.func.isRequired,
	deleteCampaign: PropTypes.func.isRequired,
};

Campaigns.propTypes = {
	getCampaigns: PropTypes.func.isRequired,
	saveCampaign: PropTypes.func.isRequired,
	deleteCampaign: PropTypes.func.isRequired,
};

export default connect(null, { getCampaigns, saveCampaign, deleteCampaign })(Campaigns);
